// =============================================================================
// FILE STATUS HEADER  (lihat /app/memory/MASTER_BLUEPRINT.md A7)
// status   : ACTIVE (Supabase migration V2 - PREVIEW ONLY)
// version  : 1.0
// updated  : 2026-07-26
// protected: NO
// changelog: Tahap 3 UX — komponen BERSAMA untuk state daftar: EmptyState (ikon +
//            judul + subjudul "langkah berikutnya" + aksi opsional), ListSkeleton
//            (placeholder saat memuat), TableSkeletonRows (skeleton di dalam <Table>),
//            dan hook useDelayedLoading (anti-kedip: skeleton hanya muncul bila
//            pemuatan > ~300ms). JANGAN definisikan ulang per halaman.
// =============================================================================
import React, { useEffect, useRef, useState } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { TableRow, TableCell } from "@/components/ui/table";

// Anti-kedip: kembalikan true HANYA jika `loading` bertahan lebih lama dari `delay`
// (default 300ms). Untuk pemuatan instan, skeleton tidak sempat muncul sehingga
// layar tidak "berkedip". Tetap true selama loading masih berjalan.
export function useDelayedLoading(loading, delay = 300) {
  const [show, setShow] = useState(false);
  const timer = useRef(null);
  useEffect(() => {
    if (loading) {
      timer.current = setTimeout(() => setShow(true), delay);
    } else {
      if (timer.current) clearTimeout(timer.current);
      setShow(false);
    }
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [loading, delay]);
  return show;
}

// Empty state seragam: ikon + judul + subjudul (menjelaskan LANGKAH BERIKUTNYA,
// bukan sekadar "tidak ada data") + tombol aksi opsional (hanya bila relevan).
export const EmptyState = ({ icon: Icon, title, description, action, testid, className = "" }) => (
  <div
    className={`flex flex-col items-center justify-center text-center gap-3 px-6 py-12 ${className}`}
    data-testid={testid}
  >
    <div className="grid place-items-center w-14 h-14 rounded-2xl bg-muted text-muted-foreground">
      {Icon && <Icon className="w-7 h-7" aria-hidden="true" />}
    </div>
    <div className="space-y-1 max-w-md">
      <h4 className="font-semibold text-foreground" data-testid={testid ? `${testid}-title` : undefined}>
        {title}
      </h4>
      {description && (
        <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
      )}
    </div>
    {action && <div className="pt-1">{action}</div>}
  </div>
);

// Skeleton generik untuk daftar berbasis kartu (mobile) / non-tabel.
export const ListSkeleton = ({ rows = 4, testid }) => (
  <div className="space-y-3" data-testid={testid}>
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="flex items-center gap-3 rounded-xl border p-3">
        <Skeleton className="h-4 w-20 shrink-0" />
        <Skeleton className="h-4 flex-1" />
        <Skeleton className="h-6 w-16 rounded-full shrink-0" />
      </div>
    ))}
  </div>
);

// SETUJU UBAH (pesan error jangan tersamar) — Bedakan pesan error API berdasarkan
// status HTTP, JANGAN selalu jatuh ke pesan cadangan "tidak ditemukan":
//   - 404          -> data BENAR-BENAR tidak ada (pakai `notFoundMsg`, atau `detail` server bila ada).
//   - 5xx          -> GANGGUAN SISTEM backend (bug/crash) — BUKAN berarti data tidak ada. Body 5xx
//                     sering teks polos (bukan JSON), jadi `detail` tidak akan ada -> jangan biarkan
//                     ini jatuh ke pesan "tidak ditemukan" (menyamarkan bug nyata dari pengguna, lihat
//                     CONTEXT_HANDOFF.md — kasus QR Tracking barang cacat, root cause: LookupError
//                     enum yang tersamar jadi "QR tidak ditemukan").
//   - 4xx lain     -> pakai `detail` dari server (validasi/otorisasi), fallback generik.
// Return: { kind: 'not_found'|'system_error'|'other', message }.
export function apiErrorInfo(r, data, notFoundMsg = "Data tidak ditemukan.") {
  const detail = data && typeof data.detail === "string" && data.detail.trim() ? data.detail : null;
  if (r.status === 404) {
    return { kind: "not_found", message: detail || notFoundMsg };
  }
  if (r.status >= 500) {
    return {
      kind: "system_error",
      message: `Gangguan sistem di server (HTTP ${r.status}). Ini BUKAN berarti data tidak ada — `
        + `coba lagi sesaat lagi, atau hubungi admin bila terus berulang.`,
    };
  }
  return { kind: "other", message: detail || `Permintaan gagal (HTTP ${r.status}).` };
}

// Skeleton yang tampil DI DALAM <Table> (mempertahankan struktur kolom).
export const TableSkeletonRows = ({ rows = 4, cols = 4, testid }) => (
  <>
    {Array.from({ length: rows }).map((_, r) => (
      <TableRow key={r} data-testid={testid ? `${testid}-${r}` : undefined}>
        {Array.from({ length: cols }).map((_, c) => (
          <TableCell key={c}>
            <Skeleton className={`h-4 ${c === 0 ? "w-16" : c === cols - 1 ? "w-20 ml-auto" : "w-full max-w-[180px]"}`} />
          </TableCell>
        ))}
      </TableRow>
    ))}
  </>
);
